import { useEffect, useMemo, useState } from "react";
import { CategoryType, MDADPredictionType, MonthEnum, PadithiEnum, PredictionType } from "../../utils/enums";
import { generateDays, isValidDateYMD } from "../../shared";
import {
    CATEGORY_OPTIONS,
    MD_AD_PREDICTION_TYPE_OPTIONS,
    MONTH_OPTIONS,
    PADITHI_OPTIONS,
    PREDICTION_TYPE_OPTIONS
} from "../../utils/options";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { getCurrentDayInTimezone, getCurrentMonthInTimezone, getCurrentYearInTimezone } from "../../utils/dateFromat";
import CommonButton from "../button/CommonButton";

const MD_CATEGORY = [CategoryType.PROMISE, CategoryType.KEY]

const AD_EXCLUDE = [
    CategoryType.PROMISE,
    CategoryType.KEY,
    CategoryType.REMEDY,
    CategoryType.CAUTION,
    CategoryType.TAG,
    CategoryType.DAY,
]

const MD_EXCLUDE = [
    CategoryType.REMEDY,
    CategoryType.CAUTION,
    CategoryType.TAG,
    CategoryType.DAY
]


const MONTH_EXCLUDE = [
    CategoryType.PROMISE,
    CategoryType.KEY,
    CategoryType.DAY,
]

export default function FilterForm({ defaultValues, isLoading, user, onSubmit }) {

    const timezone = user?.userCurrentTimezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone;

    // const birthTimezone = user?.birthDetails?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [formState, setFormState] = useState({
        ...defaultValues,
        year: getCurrentYearInTimezone(timezone),
    });

    const { conditionType, predictionType, month, year, category, day } = formState;

    const predictionTypeOptions = useMemo(() => {
        switch (conditionType) {
            case PadithiEnum.LAL_KITAB:
                return PREDICTION_TYPE_OPTIONS;

            case PadithiEnum.NADI:
            case PadithiEnum.JAIMINI:
            case PadithiEnum.PARASHARA:
                return MD_AD_PREDICTION_TYPE_OPTIONS

            default:
                return MD_AD_PREDICTION_TYPE_OPTIONS;
        }
    }, [conditionType]);

    const monthOptions = useMemo(() => {
        if (predictionType === PredictionType.MONTH) {
            return [{ label: "Current Month", value: MonthEnum.CURRENT_MONTH }, ...MONTH_OPTIONS];
        } else {
            return MONTH_OPTIONS
        }
    }, [predictionType]);

    const categoryOptions = useMemo(() => {
        switch (predictionType) {
            case PredictionType.MONTH:
                return CATEGORY_OPTIONS.filter(option => !MONTH_EXCLUDE.includes(option.value))

            case MDADPredictionType.MD:
                return CATEGORY_OPTIONS.filter(option => !MD_EXCLUDE.includes(option.value))

            case MDADPredictionType.AD:
                return CATEGORY_OPTIONS.filter(option => !AD_EXCLUDE.includes(option.value))
            default:
                return CATEGORY_OPTIONS

        }

    }, [predictionType])

    const dayOptions = useMemo(() => {
        if (predictionType === PredictionType.DAY && conditionType === PadithiEnum.LAL_KITAB) {
            const days = Object.entries(generateDays(month, year));
            return days.map(([key, value]) => ({ label: value, value: key }));
        }
        return []
    }, [month, year, predictionType, conditionType])

    // Handle padithi change
    useEffect(() => {
        let newPredictionType;

        switch (conditionType) {
            case PadithiEnum.LAL_KITAB:
                newPredictionType = PredictionType.YEAR;
                break;
            case PadithiEnum.NADI:
            case PadithiEnum.JAIMINI:
            case PadithiEnum.PARASHARA:
                newPredictionType = MDADPredictionType.MD
                break;
            default:
                newPredictionType = MDADPredictionType.MD
                break;
        }

        setFormState(prev => ({
            ...prev,
            predictionType: newPredictionType
        }));
    }, [conditionType]);

    // Handle prediction type change
    useEffect(() => {
        setFormState(prev => {
            switch (predictionType) {
                case PredictionType.MONTH:
                case MDADPredictionType.AD:
                    return {
                        ...prev,
                        month: MonthEnum.CURRENT_MONTH,
                        category: CategoryType.CAREER,
                    };

                case PredictionType.DAY:
                    return {
                        ...prev,
                        day: getCurrentDayInTimezone(timezone),
                        month: getCurrentMonthInTimezone(timezone),
                    };

                case PredictionType.YEAR:
                case MDADPredictionType.MD:
                    return {
                        ...prev,
                        category: CategoryType.PROMISE,
                    };

                default:
                    return prev;
            }
        });
    }, [predictionType, timezone]);

    useEffect(() => {
        if (!Number(year) || !Number(month) || !Number(day) || !conditionType || !predictionType) return;

        if (conditionType === PadithiEnum.LAL_KITAB && predictionType === PredictionType.DAY) {
            const validDay = isValidDateYMD(year, month, day, timezone);
            if (!validDay) {
                const day = dayOptions[dayOptions.length - 1]?.value
                setFormState(prev => ({
                    ...prev,
                    day
                }))
            }
        }
    }, [dayOptions, year, month, timezone, conditionType, predictionType, day])

    const handleChange = (field) => (event) => {
        setFormState(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formState);
    }

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        ml: 2,
                        mr: 0,
                        mt: 0,
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    {/* Padithi */}
                    <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel id="padithi-label">Padithi</InputLabel>
                        <Select
                            value={conditionType}
                            onChange={handleChange('conditionType')}
                            labelId="padithi-label"
                            label="Padithi"
                            disabled={isLoading}
                        >
                            {PADITHI_OPTIONS.map(({ label, value }) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Prediction Type */}
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="Predictiontype-label">Prediction Type</InputLabel>
                        <Select
                            value={predictionType}
                            onChange={handleChange('predictionType')}
                            labelId="Predictiontype-label"
                            label="Prediction Type"
                            disabled={isLoading}
                        >
                            {predictionTypeOptions.map(({ label, value }) => (
                                <MenuItem key={value} value={value} disabled={value === "NOTIFICATION"}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Category */}
                    {(conditionType !== PadithiEnum.LAL_KITAB || predictionType !== PredictionType.DAY) && (
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel id="category-label">Category</InputLabel>
                            <Select
                                value={category}
                                onChange={handleChange('category')}
                                labelId="category-label"
                                label="Category"
                                disabled={isLoading}
                            >
                                {categoryOptions.map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Month */}
                    {conditionType === PadithiEnum.LAL_KITAB && predictionType !== PredictionType.YEAR && (
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel id="period-label">Period Select</InputLabel>
                            <Select
                                value={month}
                                onChange={handleChange('month')}
                                labelId="period-label"
                                label="Period Select"
                                disabled={isLoading}
                            >
                                {monthOptions?.map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Day */}
                    {conditionType === PadithiEnum.LAL_KITAB && predictionType === PredictionType.DAY && (
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel id="day-label">Day</InputLabel>
                            <Select
                                value={day}
                                onChange={handleChange('day')}
                                labelId="day-label"
                                label="Day"
                                disabled={isLoading}
                            >
                                {dayOptions?.map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl>
                        <CommonButton
                            sx={{ paddingY: 2 }}
                            loading={isLoading}
                            type="submit"
                        >
                            Fetch Predictions
                        </CommonButton>
                    </FormControl>


                    {/* Year */}
                    {/* <Box sx={{ marginLeft: "auto" }}>
                    <FormControl sx={{ minWidth: 80, maxWidth: 100 }}>
                        <InputLabel id="year-label">Year</InputLabel>
                        <Select
                            value={year}
                            onChange={handleChange('year')}
                            labelId="year-label"
                            label="Year"
                            disabled
                            size="small"
                        >
                            <MenuItem value={currentYear}>{currentYear}</MenuItem>
                        </Select>
                    </FormControl>
                </Box> */}

                </Box>
            </form>
        </Box>
    );
}